import { flexRender, Row, Column } from '@tanstack/react-table'
import { Virtualizer } from '@tanstack/react-virtual'
import { TableBody, TableCell, TableRow } from '@/components/shadcn/table'
import { motion } from 'motion/react'
import clsx from 'clsx'
import { useDroppable } from '@dnd-kit/core'
import { TableBodySkeleton } from '../DataTableLoading'
import { cn } from '@/lib/utils'

const MotionTableCell = motion(TableCell)
const MotionTableRow = motion(TableRow)

interface DataTableBodyProps<TData> {
  tableBodyRef: React.RefObject<HTMLTableSectionElement>
  isRefetching: boolean
  nrOfColumns: number
  rows: Row<TData>[]
  shouldVirtualize: boolean
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>
  frozenColOffsetsById: Record<string, number>
  leafColumns: Column<TData, unknown>[]
  freezePane?: number
  compactMode: boolean
  cellMeasureRefs: React.MutableRefObject<Record<string, HTMLDivElement[]>>
  isRegrouping?: boolean
  activeDragId?: string | null
}

export function DataTableBody<TData>({
  tableBodyRef,
  isRefetching,
  nrOfColumns,
  rows,
  shouldVirtualize,
  rowVirtualizer,
  frozenColOffsetsById,
  leafColumns,
  freezePane,
  compactMode,
  cellMeasureRefs,
  isRegrouping = false,
  activeDragId,
}: DataTableBodyProps<TData>) {
  const virtualRows = shouldVirtualize ? rowVirtualizer.getVirtualItems() : []
  const paddingTop = shouldVirtualize && virtualRows.length > 0 ? virtualRows[0].start : 0
  const paddingBottom =
    shouldVirtualize && virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
      : 0

  if (isRefetching) {
    return <TableBodySkeleton nrOfColumns={nrOfColumns} />
  }

  return (
    <TableBody ref={tableBodyRef}>
      {rows.length > 0 ? (
        shouldVirtualize ? (
          // Virtualized rendering for large datasets
          <>
            {paddingTop > 0 && (
              <tr>
                <td style={{ height: `${paddingTop}px` }} colSpan={nrOfColumns} />
              </tr>
            )}

            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index]
              return (
                <DataTableRow
                  key={row.id}
                  row={row}
                  virtualIndex={virtualRow.index}
                  frozenColOffsetsById={frozenColOffsetsById}
                  leafColumns={leafColumns}
                  freezePane={freezePane}
                  compactMode={compactMode}
                  cellMeasureRefs={cellMeasureRefs}
                  isRegrouping={isRegrouping}
                  activeDragId={activeDragId}
                />
              )
            })}

            {paddingBottom > 0 && (
              <tr>
                <td style={{ height: `${paddingBottom}px` }} colSpan={nrOfColumns} />
              </tr>
            )}
          </>
        ) : (
          // Non-virtualized rendering for small datasets
          rows.map((row) => (
            <DataTableRow
              key={row.id}
              row={row}
              frozenColOffsetsById={frozenColOffsetsById}
              leafColumns={leafColumns}
              freezePane={freezePane}
              compactMode={compactMode}
              cellMeasureRefs={cellMeasureRefs}
              isRegrouping={isRegrouping}
              activeDragId={activeDragId}
            />
          ))
        )
      ) : (
        <TableRow>
          <TableCell colSpan={nrOfColumns} className="h-24 text-center">
            No results.
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  )
}

interface DataTableRowProps<TData> {
  row: Row<TData>
  virtualIndex?: number
  frozenColOffsetsById: Record<string, number>
  leafColumns: Column<TData, unknown>[]
  freezePane?: number
  compactMode: boolean
  cellMeasureRefs: React.MutableRefObject<Record<string, HTMLDivElement[]>>
  isRegrouping?: boolean
  activeDragId?: string | null
  isOverlay?: boolean
  className?: string
}

export function DataTableRow<TData>({
  row,
  virtualIndex,
  frozenColOffsetsById,
  leafColumns,
  freezePane,
  compactMode,
  cellMeasureRefs,
  isRegrouping = false,
  activeDragId,
  isOverlay = false,
  className,
}: DataTableRowProps<TData>) {
  const isSubRow = row.depth > 0

  const rowData = row.original as Record<string, unknown>
  const rowId = String(rowData?.id || row.id)
  const isDragging = activeDragId === rowId

  const { setNodeRef, isOver } = useDroppable({
    id: rowId,
    disabled: !isRegrouping || isSubRow || isDragging || isOverlay, // Cannot drop on itself, subrows, or the overlay itself
  })

  return (
    <MotionTableRow
      ref={isOverlay ? undefined : setNodeRef}
      data-state={row.getIsSelected() && 'selected'}
      data-index={virtualIndex}
      data-row-id={rowId}
      data-depth={row.depth}
      className={cn(
        'odd:bg-offwhite/50 hover:bg-primary/5',
        {
          '!bg-secondary/10 hover:!bg-secondary/15': isSubRow,
          '!bg-secondary/30': !isSubRow && (row.getCanExpand() || !!rowData.isGroup),
          // Use pseudo-element for outline to ensure it's visible over sticky cells
          'after:border-1 relative after:pointer-events-none after:absolute after:inset-0 after:z-[50] after:border-primary after:bg-primary/40':
            isOver && !isDragging,
          'opacity-30': isDragging,
          // Ensure overlay has consistent background and no hover effects
          'bg-offwhite hover:bg-offwhite': isOverlay,
        },
        className
      )}
      initial={isSubRow ? { opacity: 0 } : undefined}
      animate={isSubRow ? { opacity: 1 } : undefined}
      transition={{ duration: 0.2, delay: 0.1 }}
    >
      {row.getVisibleCells().map((cell) => {
        const isActionCol = cell.column.id === 'actions' || cell.column.id === 'rowActions'
        const isExpanderCol = cell.column.id === 'expander'
        const isFrozen = freezePane && frozenColOffsetsById[cell.column.id] !== undefined
        const leftOffset = isFrozen ? frozenColOffsetsById[cell.column.id] : undefined
        const isLastFrozen = isFrozen && freezePane && leafColumns[freezePane - 1]?.id === cell.column.id

        return (
          <MotionTableCell
            layout
            className={clsx('border-b-[0.5px] last:border-b-[0.5px]', {
              'bg-offwhite sticky right-0 z-[9] border-l': isActionCol,
              'bg-offwhite sticky z-[9] border-r': isFrozen,
              'drop-shadow-sm': isLastFrozen,
              'py-0.5': compactMode,
              // Add left border to the expander column for sub-rows
              'bg-secondary/30 backdrop-blur':
                (isSubRow && (isExpanderCol || isFrozen)) ||
                (!isSubRow && (isExpanderCol || isFrozen) && (row.getCanExpand() || !!rowData.isGroup)),
              // Add subtle background to expander column for group rows
              // 'bg-secondary/30 backdrop-blur':
              //   !isSubRow && isExpanderCol && row.getCanExpand(),
            })}
            style={
              isFrozen
                ? {
                    left: `${leftOffset}px`,
                  }
                : undefined
            }
            key={cell.id}
          >
            <div
              ref={(el) => {
                if (!isActionCol) {
                  const list = cellMeasureRefs.current[cell.column.id] || []
                  if (el) {
                    if (list.length === 0) cellMeasureRefs.current[cell.column.id] = list
                    list.push(el)
                  }
                }
              }}
              className={clsx({
                'flex justify-end': isActionCol,
                'flex h-full': !isActionCol,
                'pl-4': row.depth > 0 && !isExpanderCol && cell.column.id === 'title', // Indent only title column for subrows
              })}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </div>
          </MotionTableCell>
        )
      })}
    </MotionTableRow>
  )
}
